import clsx from "clsx";
import { type InputHTMLAttributes, type ReactNode, useState } from "react";
import { MdOutlineVisibility, MdOutlineVisibilityOff } from "react-icons/md";

import type { InputProps } from "../../../interfaces/input";
import { LoadingSkeleton } from "../../LoadingSkeleton";
import { InputError } from "../InputError";
import { Tooltip } from "../../Tooltip";

type Props = InputHTMLAttributes<HTMLTextAreaElement> &
  InputProps & {
    labelTooltip?: ReactNode;
  };

export const InputText = ({
  type,
  field,
  label,
  className,
  errorMessage,
  loadingValue,
  inputClassName: errorClassName,
  labelTooltip,
  ...attributes
}: Props) => {
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);

  const VisibilityIcon = isPasswordVisible ? MdOutlineVisibilityOff : MdOutlineVisibility;

  const baseClassName = clsx("w-full", {
    "outline-feedback-error": errorMessage,
  });

  return (
    <div className={clsx("flex flex-col", className)}>
      {label && (
        <div className="mb-1 flex items-center gap-1 lg:mb-0.5">
          <label className={clsx(attributes.disabled && "opacity-50")} htmlFor={field}>
            <b>{label}</b>
          </label>
          {labelTooltip && (
            <Tooltip type="info" position="bottom" popoverClassName="z-50">
              {labelTooltip}
            </Tooltip>
          )}
        </div>
      )}

      {loadingValue ? (
        <LoadingSkeleton kind="input" />
      ) : (
        <>
          <div className="relative flex items-center">
            {type === "textarea" ? (
              <textarea {...attributes} className={clsx(baseClassName, "min-h-11 resize-y")} id={field} name={field} />
            ) : (
              <input
                {...attributes}
                type={isPasswordVisible ? "text" : type}
                className={clsx(baseClassName, {
                  "pr-7": type === "password",
                })}
                id={field}
                name={field}
              />
            )}

            {type === "password" && (
              <div
                role="none"
                className="absolute right-2 cursor-pointer"
                onClick={() => setIsPasswordVisible(!isPasswordVisible)}
              >
                <VisibilityIcon size="1.5rem" />
              </div>
            )}
          </div>
          {type === "textarea" && attributes.maxLength && (
            <span className="mt-1 self-end text-xs text-details">
              {String(attributes.value ?? "").length}/{attributes.maxLength}
            </span>
          )}
        </>
      )}

      <InputError message={errorMessage} className={errorClassName} />
    </div>
  );
};
